import {a, div, vbox, vspacer} from '@xh/hoist/cmp/layout';
import {ElementFactory, HoistModel, XH} from '@xh/hoist/core';
import {required} from '@xh/hoist/data';
import {Icon} from '@xh/hoist/icon';
import {bindable} from '@xh/hoist/mobx';
import {ReactElement} from 'react';
import './PasswordLinks.scss';

/**
 * Emailed set-password links, and the login screen's "Forgot password?". Both run before sign-in, so they use
 * modal messages, which the AppContainer shows at any point in the app's lifecycle.
 *
 * A link opens the app with the token in the URL fragment (`#setPassword=...`), which the browser never sends to
 * a server, so it stays out of request logs.
 */

/**
 * The platform's TextInput factory - desktop or mobile - which each app's entry point sets, as Hoist won't load
 * the other platform's components.
 */
export type TextInputFactory = ElementFactory<any>;

const TOKEN_PARAM = 'setPassword';

/** The token of a set-password link this page was opened with, removed from the address bar - or null. */
export function takeSetPasswordToken(): string {
    const params = new URLSearchParams(window.location.hash.slice(1)),
        token = params.get(TOKEN_PARAM);
    if (!token) return null;

    params.delete(TOKEN_PARAM);
    const hash = params.toString();
    window.history.replaceState(null, '', window.location.pathname + window.location.search + (hash ? '#' + hash : ''));
    return token;
}

/**
 * Asks for a new password for the link's user and sets it. Returns the login token the server answers with, or
 * null when the link is no good or the user cancels - the login form follows.
 */
export async function setPasswordFromLinkAsync(svcUrl: string, token: string, textInput: TextInputFactory): Promise<string> {
    let info: {username: string; minPasswordLength: number};
    try {
        info = await postAsync(svcUrl, '/login/passwordToken', {token});
    } catch (e) {
        await XH.alert({
            title: 'Link expired',
            icon: Icon.warning(),
            message: `${serverMessage(e)} Use "Forgot your password?" on the login screen to get a new link.`
        });
        return null;
    }

    let warning: string = null;
    while (true) {
        const model = new NewPasswordModel(),
            {minPasswordLength} = info;
        const confirmed = await XH.prompt<string>({
            title: 'Set Your Password',
            className: 'rzx-set-password',
            icon: Icon.unlock(),
            message: vbox(
                div(`Choose a password for ${info.username}, at least ${minPasswordLength} characters long.`),
                vspacer(10),
                passwordInput(textInput, {model, bind: 'password', placeholder: 'New password', autoFocus: true}),
                div({className: 'xh-login__warning', item: warning, omit: !warning})
            ),
            input: {
                item: passwordInput(textInput, {placeholder: 'Confirm password', width: '100%'}),
                rules: [
                    required,
                    () => (model.password.length < minPasswordLength ? `Use at least ${minPasswordLength} characters` : null),
                    ({value}) => (value !== model.password ? 'The passwords do not match' : null)
                ]
            },
            confirmProps: {text: 'Set Password', icon: Icon.check(), intent: 'primary'},
            cancelProps: {text: 'Cancel'}
        });
        if (!confirmed) return null;

        try {
            const {token: idToken} = await postAsync(svcUrl, '/login/setPassword', {token, password: model.password});
            XH.successToast('Your password is set');
            return idToken;
        } catch (e) {
            // e.g. the link expired while the dialog was open - try again, saying why
            warning = serverMessage(e);
        }
    }
}

/** The login screen's link to ask for a set-password email. */
export function forgotPasswordLink(svcUrl: string): ReactElement {
    return a({
        items: [Icon.unlock(), ' Forgot your password?'],
        href: '#',
        onClick: e => {
            e.preventDefault();
            requestPasswordLinkAsync(svcUrl);
        }
    });
}

async function requestPasswordLinkAsync(svcUrl: string) {
    const usernameOrEmail = await XH.prompt<string>({
        title: 'Forgot Your Password?',
        icon: Icon.unlock(),
        message: "Enter your username or email address, and we'll email you a link to set a new password.",
        input: {rules: [required]},
        confirmProps: {text: 'Send Link', icon: Icon.mail(), intent: 'primary'},
        cancelProps: {text: 'Cancel'}
    });
    if (!usernameOrEmail) return;

    try {
        const {message} = await postAsync(svcUrl, '/login/forgotPassword', {usernameOrEmail});
        await XH.alert({title: 'Check Your Email', icon: Icon.mail(), message});
    } catch (e) {
        XH.handleException(e, {title: 'Error requesting a password link'});
    }
}

//------------------------
// Implementation
//------------------------
class NewPasswordModel extends HoistModel {
    @bindable accessor password = '';
}

function passwordInput(textInput: TextInputFactory, props: Record<string, any>) {
    return textInput({type: 'password', autoComplete: 'new-password', commitOnChange: true, width: null, ...props});
}

function postAsync(svcUrl: string, path: string, body: object) {
    return XH.fetchService.postJson({url: svcUrl + path, body, headers: {'Content-Type': 'application/json'}});
}

function serverMessage(e: any): string {
    return e.serverDetails?.error?.message ?? e.message;
}
